/**
 * Application orchestrator — pure simulation math for one tick (App.tsx §2, lines 722–729).
 * Encapsulates connectivity refresh + calculateTickMetrics; no UI/framework deps.
 */
import { SimulationEngine } from '../../domain/services/SimulationEngine';
import type { CalculateConnectivityUseCase } from './CalculateConnectivityUseCase';
import {
  OrgImpactApplier,
  type OrgTickData,
} from '../../domain/services/OrgImpactApplier';
import type { OrgImpactMetrics } from '../../../utils/orgImpactCalculator';
import type { Grid, CityStats, StatDeltas, SimulationBreakdown } from '../../../types';

export interface ProcessGameTickInput {
  grid: Grid;
  stats: CityStats;
  tickNumber: number;
}

export interface ProcessGameTickOutput {
  grid: Grid;
  netBudget: number;
  netComp: number;
  dailyThreat: number;
  deltas: StatDeltas;
  buildingCounts: Record<string, number>;
  revenueHalted: boolean;
  violations: string[];
  violationTiles: { x: number; y: number }[];
  breakdown: SimulationBreakdown;
  /** Present when org impact provider is wired (HR / TPRM / audit modifiers) */
  orgImpact?: OrgImpactMetrics;
}

export interface OrgDataProvider {
  getOrgTickData(): OrgTickData;
}

export interface ProcessGameTickDependencies {
  simulation: SimulationEngine;
  calculateConnectivity: CalculateConnectivityUseCase;
  orgImpact?: OrgImpactApplier;
  orgDataProvider?: OrgDataProvider;
}

export class ProcessGameTickOrchestrator {
  constructor(private readonly deps: ProcessGameTickDependencies) {}

  execute(input: ProcessGameTickInput): ProcessGameTickOutput {
    const connectedGrid = this.deps.calculateConnectivity.execute({ grid: input.grid }).grid;

    const {
      netBudget,
      netComp: baseNetComp,
      dailyThreat: baseDailyThreat,
      deltas: baseDeltas,
      buildingCounts,
      revenueHalted,
      violations,
      violationTiles,
      breakdown,
    } = this.deps.simulation.calculateTickMetrics(connectedGrid, input.stats, input.tickNumber);

    let netComp = baseNetComp;
    let dailyThreat = baseDailyThreat;
    let deltas = baseDeltas;
    let orgImpact: OrgImpactMetrics | undefined;

    if (this.deps.orgImpact && this.deps.orgDataProvider) {
      const orgData = this.deps.orgDataProvider.getOrgTickData();
      const adjusted = this.deps.orgImpact.applyToTickMetrics(netComp, dailyThreat, orgData);
      netComp = adjusted.netComp;
      dailyThreat = adjusted.dailyThreat;
      orgImpact = adjusted.adjustment.metrics;
      deltas = {
        ...deltas,
        compliance: deltas.compliance + adjusted.adjustment.complianceDelta,
        threat: deltas.threat + adjusted.adjustment.threatDelta,
      };
    }

    return {
      grid: connectedGrid,
      netBudget,
      netComp,
      dailyThreat,
      deltas,
      buildingCounts,
      revenueHalted,
      violations,
      violationTiles,
      breakdown,
      orgImpact,
    };
  }
}